import { useEffect, useState, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  FormHelperText,
  Input,
  Textarea,
  VStack,
  HStack,
  Text,
  Grid,
  Spinner,
} from '@chakra-ui/react'
import { apiClient } from '@/lib/api/client'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { TreeDropdown, type TreeDropdownRef } from '@/components/forms/TreeDropdown'
import {
  createCategorySchema,
  updateCategorySchema,
  type CreateCategoryFormData,
  type UpdateCategoryFormData,
} from '@/lib/validations/auth'

interface CategoryFormProps {
  categoryId?: number
  onSuccess?: () => void
  onCancel?: () => void
}

type CategoryFormValues = CreateCategoryFormData | UpdateCategoryFormData

export function CategoryForm({ categoryId, onSuccess, onCancel }: CategoryFormProps) {
  const navigate = useNavigate()
  const { showError, showSuccess } = useToastNotification()
  const categoryRef = useRef<TreeDropdownRef>(null)

  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const isEditMode = !!categoryId

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<CategoryFormValues>({
    resolver: zodResolver(isEditMode ? updateCategorySchema : createCategorySchema),
    defaultValues: {
      descriptions: '',
      parent_id: null,
    },
  })

  const loadOptions = useCallback(
    (parentId: number | null, callback: (data: any[]) => void) => {
      const url = `/category-management/categories/dropdown${parentId ? `/${parentId}` : ''}`
      apiClient
        .get(url)
        .then(({ data }) => {
          let categoryData = data?.data || data || []
          categoryData = Array.isArray(categoryData) ? categoryData : []

          if (isEditMode && categoryId) {
            const filterCategory = (items: any[]): any[] =>
              items
                .filter((item) => item.id !== categoryId)
                .map((item) => ({
                  ...item,
                  children: item.children ? filterCategory(item.children) : item.children,
                }))
            categoryData = filterCategory(categoryData)
          }

          callback(categoryData)
        })
        .catch((error: any) => {
          showError(error)
          callback([])
        })
    },
    [categoryId, isEditMode, showError]
  )

  useEffect(() => {
    if (isEditMode && categoryId) {
      setIsLoading(true)
      apiClient
        .get(`/category-management/categories/${categoryId}`)
        .then(({ data }) => {
          const categoryData = data?.data || data || {}

          let parentId: number | null = null
          if (categoryData.parent_id) {
            parentId = categoryData.parent_id
          } else if (categoryData.parent_category?.id) {
            parentId = categoryData.parent_category.id
          }

          reset({
            name: categoryData.name || '',
            code: categoryData.code || '',
            descriptions: categoryData.descriptions || '',
            parent_id: parentId,
          })
        })
        .catch((error: any) => {
          showError(error)
        })
        .finally(() => {
          setIsLoading(false)
        })
    }
  }, [categoryId, isEditMode, reset, showError])

  const onSubmit = async (data: CategoryFormValues) => {
    setIsSubmitting(true)

    try {
      const parentValue = categoryRef.current?.getValue()
      const payload = {
        name: data.name,
        code: data.code,
        descriptions: data.descriptions || null,
        parent_id: parentValue?.id || null,
      }

      if (isEditMode) {
        await apiClient.put(`/category-management/categories/${categoryId}`, payload)
        showSuccess('Success', 'Category updated successfully')
      } else {
        await apiClient.post('/category-management/categories', payload)
        showSuccess('Success', 'Category created successfully')
      }

      if (onSuccess) {
        onSuccess()
      } else {
        navigate('/category-management/categories')
      }
    } catch (error: any) {
      showError(error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    if (onCancel) {
      onCancel()
    } else {
      navigate('/category-management/categories')
    }
  }

  if (isLoading) {
    return (
      <VStack spacing={4} py={8}>
        <Spinner size="xl" color="blue.500" />
        <Text>Loading category...</Text>
      </VStack>
    )
  }

  return (
    <Box as="form" onSubmit={handleSubmit(onSubmit)}>
      <VStack spacing={4} align="stretch">
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Name <Text as="span" color="red.500">*</Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.name}>
            <Input {...register('name')} placeholder="e.g. Technology, Business, Health" />
            <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
            <FormHelperText>The name is how it appears on your site.</FormHelperText>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Code <Text as="span" color="red.500">*</Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.code}>
            <Input {...register('code')} placeholder="e.g. technology, business, health" />
            <FormErrorMessage>{errors.code?.message}</FormErrorMessage>
            <FormHelperText>The code is used in URLs. Only letters, numbers, and hyphens.</FormHelperText>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Description
          </FormLabel>
          <FormControl isInvalid={!!errors.descriptions}>
            <Textarea
              {...register('descriptions')}
              placeholder="Enter category description..."
              rows={4}
            />
            <FormErrorMessage>{errors.descriptions?.message}</FormErrorMessage>
            <FormHelperText>Optional description for this category.</FormHelperText>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Parent Category
          </FormLabel>
          <FormControl>
            <TreeDropdown
              ref={categoryRef}
              values={watch('parent_id') ? JSON.stringify({ id: watch('parent_id') }) : null}
              loadOptions={loadOptions}
              placeholder="Select Parent Category (optional)"
              onChange={(value) => {
                try {
                  const parsed = JSON.parse(value)
                  setValue('parent_id', parsed.id ?? null, { shouldValidate: true })
                } catch {
                  setValue('parent_id', null, { shouldValidate: true })
                }
              }}
            />
            <FormHelperText>
              Categories can have a hierarchy. You can set a parent category to create subcategories.
            </FormHelperText>
          </FormControl>
        </Grid>

        <HStack spacing={4} justify="flex-end" pt={4}>
          <Button variant="outline" onClick={handleCancel} isDisabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            colorScheme="blue"
            isLoading={isSubmitting}
            loadingText={isEditMode ? 'Saving...' : 'Creating...'}
          >
            {isEditMode ? 'Save' : 'Create Category'}
            {isSubmitting && (
              <Text as="span" ml={2} fontSize="sm">
                ...
              </Text>
            )}
          </Button>
        </HStack>
      </VStack>
    </Box>
  )
}


